import { Injectable } from "@angular/core";
import { IDestinationsProvider } from "app/js/src/domain/clipping-studio/publish/i-destinations-provider";
import {
  SocialAccount,
  DestinationType,
} from "app/js/src/domain/clipping-studio/publish/publish-models";
import { DestinationsRepository } from "app/js/src/infrastructure/repositories/destinations/destinations.repository";

/** Local SVGs only when the API omits `icon_asset_key` (catalog icon URL). */
const PUBLISHING_STATUS_ASSETS = "/assets/publishing-status";
const DESTINATION_TYPE_FALLBACK_ICON: Record<DestinationType, string> = {
  yt: `${PUBLISHING_STATUS_ASSETS}/youtube.svg`,
  fb: `${PUBLISHING_STATUS_ASSETS}/facebook.svg`,
  ttv: `${PUBLISHING_STATUS_ASSETS}/twitch.svg`,
  ig: `${PUBLISHING_STATUS_ASSETS}/instagram.svg`,
  s3: `${PUBLISHING_STATUS_ASSETS}/globe.svg`,
  tt: `${PUBLISHING_STATUS_ASSETS}/globe.svg`,
};

/** CSS class per DestinationType (brand tint on the icon wrapper). */
const DESTINATION_TYPE_STYLE: Record<DestinationType, { iconCssClass: string }> = {
  yt:  { iconCssClass: "platform--youtube" },
  fb:  { iconCssClass: "platform--facebook" },
  ttv: { iconCssClass: "platform--twitch" },
  ig:  { iconCssClass: "platform--instagram" },
  s3:  { iconCssClass: "platform--website" },
  tt:  { iconCssClass: "platform--tiktok" },
};

/** Maps numeric `platform_id` from the API to clipping-studio `DestinationType` (minimal client mapping). */
function platformIdToType(platformId: number): DestinationType {
  switch (platformId) {
    case 1:
      return 'yt';
    case 2:
      return 'tt';
    case 3:
      return 'ig';
    case 4:
      return 'fb';
    default:
      return 's3';
  }
}

/**
 * Adapts {@link DestinationsRepository} tenant rows to {@link IDestinationsProvider} (flat SocialAccount[]).
 */
@Injectable()
export class ClippingStudioDestinationsAdapterService extends IDestinationsProvider {
  constructor(private destinationsRepository: DestinationsRepository) {
    super();
  }

  getDestinations(): SocialAccount[] {
    const rows = this.destinationsRepository.getCachedTenantPlatformsWithDestinations();
    const result: SocialAccount[] = [];
    for (const row of rows) {
      const type = platformIdToType(row.platform.platformId);
      const apiIconUrl = row.platform.iconUrl?.trim() ?? "";
      const icon = apiIconUrl || DESTINATION_TYPE_FALLBACK_ICON[type];
      for (const destination of row.destinations) {
        const { iconCssClass } = DESTINATION_TYPE_STYLE[type];
        result.push({
          id: destination.uid,
          platform: row.platform.name,
          name: destination.displayName,
          connected: destination.status === "Connected",
          type,
          icon,
          iconCssClass,
        });
      }
    }
    return result;
  }
}
